export const ensureArray = <T>(value: T | T[] | null | undefined): T[] => {
  if (value === undefined || value === null) {
    return [];
  }

  return Array.isArray(value) ? value : [value];
};

export const formatDateToISO = (value: Date | string | number): string => {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

export const one = (
  unit: "minute" | "hour" | "day" | "year",
): number => {
  switch (unit) {
    case "minute":
      return 60 * 1000;
    case "hour":
      return 60 * one("minute");
    case "day":
      return 24 * one("hour");
    case "year":
      return 365 * one("day");
  }
};

export const timeToNumber = (value: unknown): number => {
  if (typeof value === "number") {
    return value;
  }

  if (typeof value !== "string") {
    return 0;
  }

  const [hoursText = "0", minutesText = "0"] = value.split(":");
  const hours = Number(hoursText);
  const minutes = Number(minutesText);

  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return 0;
  }

  return hours * 60 + minutes;
};


export const numberToTime = (value: unknown): string => {
  const totalMinutes = typeof value === "number" ? value : Number(value);

  if (Number.isNaN(totalMinutes)) {
    return "00:00";
  }

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
};

export const isBefore = (
  value: Date | string | number,
  compareTo: Date | string | number,
): boolean => {
  return new Date(value).getTime() < new Date(compareTo).getTime();
};

export const isFunction = <T extends Function = Function>(
  value: unknown,
): value is T => typeof value === "function";

export const startCase = (value: string): string => {
  return value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_.-]+/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
};

// Mirrors the Flex API's own phone validator: digits with an optional leading
// zero and a length of 10-14. Normalising here keeps "+44" and spaced numbers
// from being rejected upstream.
const UK_PHONE_RE = /^(0)?[0-9]+$/;

export const normalizePhone = (value: string): string => {
  const digits = value.replace(/\D/g, "");
  if (!digits) return "";

  if (digits.startsWith("44") && digits.length >= 12) {
    return `0${digits.slice(2).replace(/^0+/, "")}`;
  }

  return digits.startsWith("0") ? digits : `0${digits}`;
};

export const isValidUkContactPhone = (value: string): boolean => {
  const normalized = normalizePhone(value);

  return (
    normalized.length > 9 &&
    normalized.length < 15 &&
    UK_PHONE_RE.test(normalized)
  );
};

export const lowerCase = (value: string): string => {
  return value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_.-]+/g, " ")
    .trim()
    .toLowerCase();
};
